import type { ComponentChildren, ComponentType } from 'preact';
import type { LucideProps } from 'lucide-preact';
import { Check } from 'lucide-preact';
import { Icon } from './Icon';
import { cn } from '@/lib/cn';

export interface RadioCardOption<TValue extends string | number = string> {
  value: TValue;
  label: ComponentChildren;
  description?: ComponentChildren;
  icon?: ComponentType<LucideProps>;
  disabled?: boolean;
}

export interface RadioCardGroupProps<TValue extends string | number = string> {
  value: TValue | null | undefined;
  options: ReadonlyArray<RadioCardOption<TValue>>;
  onChange: (value: TValue) => void;
  /** Layout en grid.  Default 2 cols en md+. */
  columns?: 1 | 2 | 3;
  /** Para form fields donde el `<FormField>` ya provee `aria-labelledby`. */
  'aria-labelledby'?: string;
  'aria-label'?: string;
  className?: string;
}

const COLS = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 md:grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3',
} as const;

/**
 * Grupo de radios renderizados como cards seleccionables — el patrón
 * "elige uno de N modelos" del wizard de setup.  Sustituye los
 * `<button role="radio">` con styling ad-hoc que vivían en
 * `BusinessTypeStep`, `CacAStep`, `LtgpStep`.
 *
 * Visual: borde sutil → brand tint + check icon cuando seleccionada;
 * `bg-surface` por defecto, hover oscurece el borde.
 */
export function RadioCardGroup<TValue extends string | number = string>({
  value,
  options,
  onChange,
  columns = 2,
  'aria-labelledby': ariaLabelledBy,
  'aria-label': ariaLabel,
  className,
}: RadioCardGroupProps<TValue>) {
  return (
    <div
      role="radiogroup"
      aria-labelledby={ariaLabelledBy}
      aria-label={ariaLabel}
      className={cn('grid gap-2', COLS[columns], className)}
    >
      {options.map((opt) => {
        const checked = opt.value === value;
        const IconCmp = opt.icon;
        return (
          <button
            key={String(opt.value)}
            type="button"
            role="radio"
            aria-checked={checked}
            disabled={opt.disabled}
            onClick={() => !opt.disabled && onChange(opt.value)}
            className={cn(
              'flex flex-col items-start gap-1.5 rounded-lg border bg-surface px-4 py-3 text-left',
              'transition-colors duration-fast outline-none',
              'focus-visible:shadow-focus focus-visible:ring-1 focus-visible:ring-brand',
              'disabled:cursor-not-allowed disabled:opacity-50',
              checked
                ? 'border-brand bg-brand-tint shadow-xs'
                : 'border-border-subtle hover:border-border hover:bg-surface-hover',
            )}
          >
            <div className="flex w-full items-center justify-between gap-2">
              <span className="inline-flex items-center gap-2 text-13 font-semibold text-fg-strong">
                {IconCmp ? (
                  <Icon as={IconCmp} size={14} aria-hidden="true" className={checked ? 'text-brand' : 'text-fg-muted'} />
                ) : null}
                {opt.label}
              </span>
              {checked ? <Icon as={Check} size={14} className="text-brand" aria-hidden="true" /> : null}
            </div>
            {opt.description ? (
              <span className="text-2xs leading-snug text-fg-muted">{opt.description}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
